import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Row,
  Col,
  message
} from 'antd';
import apiService from "services/api/apiService";
import { JobPosting } from '../../types/recruitment';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface JobPostingFormProps {
  job?: JobPosting | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const JobPostingForm: React.FC<JobPostingFormProps> = ({ job, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (job) {
      form.setFieldsValue({
        ...job,
        application_deadline: job.application_deadline ? dayjs(job.application_deadline) : null,
        salary_min: job.salary_min ? parseFloat(job.salary_min) : 0,
        salary_max: job.salary_max ? parseFloat(job.salary_max) : 0,
      });
    }
  }, [job, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      const jobData = {
        job_id: values.job_id || `JOB${Date.now()}`,
        title: values.title,
        department: values.department,
        location: values.location,
        employment_type: values.employment_type || 'full_time',
        salary_min: values.salary_min ? values.salary_min.toString() : '0.00',
        salary_max: values.salary_max ? values.salary_max.toString() : '0.00',
        description: values.description || '',
        requirements: values.requirements || '',
        application_deadline: values.application_deadline ? 
          values.application_deadline.format('YYYY-MM-DD') : null,
        status: values.status || 'draft',
        positions_available: values.positions_available || 1
      };

      let response;
      if (job) {
        response = await apiService.updateJobPosting(job.id.toString(), jobData);
      } else {
        response = await apiService.createJobPosting(jobData);
      }

      if (response.success) {
        message.success(job ? 'Job posting updated successfully' : 'Job posting created successfully');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.error || 'Operation failed');
      }
    } catch (error: any) {
      console.error('Job posting operation error:', error);
      message.error(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        status: 'draft',
        employment_type: 'full_time',
        positions_available: 1,
        salary_min: 0,
        salary_max: 0
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="title"
            label="Job Title"
            rules={[{ required: true, message: 'Please enter job title' }]}
          >
            <Input placeholder="Enter job title" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true, message: 'Please enter department' }]}
          >
            <Input placeholder="Enter department" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="location"
            label="Location"
            rules={[{ required: true, message: 'Please enter location' }]}
          >
            <Input placeholder="Enter location" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="employment_type" label="Employment Type">
            <Select>
              <Option value="full_time">Full Time</Option>
              <Option value="part_time">Part Time</Option>
              <Option value="contract">Contract</Option>
              <Option value="intern">Internship</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="positions_available" label="Positions Available">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="salary_min" label="Minimum Salary (₦)">
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter minimum salary"
              min={0}
              step={10000}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="salary_max" label="Maximum Salary (₦)">
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter maximum salary"
              min={0}
              step={10000}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="application_deadline" label="Application Deadline">
            <DatePicker style={{ width: '100%' }} placeholder="Select deadline" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="draft">Draft</Option>
              <Option value="active">Active</Option>
              <Option value="closed">Closed</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="job_id" label="Job ID">
            <Input placeholder="Auto-generated if empty" disabled={!!job} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="description" label="Job Description">
        <TextArea rows={4} placeholder="Enter job description" />
      </Form.Item>

      <Form.Item name="requirements" label="Requirements">
        <TextArea rows={4} placeholder="Enter job requirements" />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {job ? 'Update Job Posting' : 'Create Job Posting'}
          </Button>
          <Button onClick={onCancel}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default JobPostingForm;
